import React, { useState, useEffect, useContext, useRef } from "react";
import Stack from "@mui/material/Stack";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Box, Grid, Button, Link } from "@mui/material";
import Drawer from "../../../Components/Common/DrawerLayout/Drawer";
import { AuthContext } from "../../../context/AuthContext";
import { SnackBarContext } from "../../../context/SnackBarContext";
import { CREATE_CLUB_SA, EDIT_CLUB } from "../SuperAdminService";
import { DELETE_IMAGE, UPLOAD_IMAGE } from "../../Teams/TeamsService";
import {
  DELETE_FILE,
  UPLOAD_CLUB_GDPR_FILE,
} from "../../../graphql/fileGraphql";
import Form from "../../../Components/Form/Form";
import { formValidator, isEmpty } from "../../../Components/Form/validators";
import TextFieldRenderer from "../../../Components/Form/TextFieldRenderer";
import SelectRenderer from "../../../Components/Form/SelectRenderer";
import { makeStyles } from "@mui/styles";
import { colors } from "../../../theme/colors";
import { fonts } from "../../../theme/fonts";
import {
  brandColorsOptions,
  countries,
  FILE_TYPES,
  imageMimeTypesDropzone,
  sports,
  USER_ROLES,
} from "../../../utility/Enum";
import { useTranslation } from "../../../context/Internationalization";
import { axiosGet, axiosAuthenticatedPost } from "../../../api";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import { getStorage } from "../../../auth/utils";
import MultipleFilesUpload from "../../../Components/Form/MultipleFilesUpload";
import {
  DrawerBody,
  DrawerFooter,
  DrawerLayout,
  DrawerSection,
  DrawerTitleHeader,
  getTransactionalDrawerPaperProps,
} from "../../../Components/Common/DrawerLayout/DrawerLayout";

const useStyles = makeStyles({
  identityComponentDetails: {
    ...fonts.text.md.normal,
    color: colors.Grey[500],
  },
  gdprActions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "flex-start",
  },
});

const isImpersonating = Boolean(getStorage().getItem("impersonate"));

const schema = (t, roles) => [
  {
    label: t("club_name"),
    name: "name",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    label: t("abbreviation"),
    name: "abbreviation",
    maxLength: 3,
    renderer: TextFieldRenderer,
    row: 2,
  },
  {
    label: t("founded"),
    name: "founded",
    renderer: TextFieldRenderer,
    row: 3,
  },
  {
    label: t("country"),
    name: "country",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    options:
      countries?.map((country) => ({
        value: country.label,
        text: country.label,
      })) || [],
    renderer: SelectRenderer,
    row: 4,
  },
  {
    label: t("sport"),
    name: "sport",
    options: sports(t),
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    row: 5,
    renderer: SelectRenderer,
  },
  {
    label: t("brand_color"),
    name: "brandColor",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: SelectRenderer,
    options: brandColorsOptions(t) || [],
    colorPicker: true,
    row: 6,
  },
  roles.includes(USER_ROLES.SUPER_ADMIN) ||
  roles.includes(USER_ROLES.CLUB_ADMIN)
    ? {
        label: "",
        name: "clubLogo",
        renderer: MultipleFilesUpload,
        singleFileUpload: true,
        acceptedFileTypes: imageMimeTypesDropzone,
        row: 9,
      }
    : {},
  // {
  //   label: 'Location',
  //   name: 'location',
  //   disabled: selectedClub,
  //   renderer: LocationRenderer,
  //   row: 10
  // },
];

const initialState = {
  name: "",
  abbreviation: "",
  founded: "",
  country: "Greece",
  sport: "",
  brandColor: "#1B2028",
  clubLogo: null,
  initialClubLogo: null,
  location: undefined,
  gdpr: "",
};

const populateData = (club) => {
  const _initialState = { ...initialState };
  if (club) {
    _initialState.name = club.name;
    _initialState.abbreviation = club.abbreviation || "";
    _initialState.founded = club.founded || "";
    _initialState.country = club.country || "";
    _initialState.sport = club.sport || "";
    _initialState.clubLogo = club.logo ? [club.logo] : null;
    _initialState.initialClubLogo = _initialState.clubLogo;
    _initialState.location = {
      address: club.location?.address,
      lat: club.location?.coordinates[1],
      lng: club.location?.coordinates[0],
    };
    _initialState.gdpr = club.gdpr || "";
  }

  return _initialState;
};

const ClubsDrawer = ({ openDrawer, onCloseDrawer, onExited, selectedClub, getClubs, title }) => {
  const { authState } = useContext(AuthContext);
  const snackBar = useContext(SnackBarContext);
  const [values, setValues] = useState(populateData(selectedClub));
  const [gdprUploading, setGdprUploading] = useState(false);
  const gdprInputRef = useRef(null);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const roles = authState.user.roles;
  const _schema = schema(t, roles);
  const clubDetailsSchema = _schema.filter(
    (field) =>
      field?.name && !["brandColor", "clubLogo"].includes(field.name)
  );
  const clubBrandingSchema = _schema.filter((field) =>
    ["brandColor", "clubLogo"].includes(field?.name)
  );

  const styles = useStyles();

  useEffect(() => {
    if (selectedClub) {
      setValues(populateData(selectedClub));
      getThemeColor(selectedClub._id);
    } else {
      setValues(initialState);
    }
  }, [selectedClub?._id]);

  const getThemeColor = (clubId) => {
    axiosGet(`/theme/${clubId}`)
      .then((response) => {
        const { primaryColor } = response.data;
        setValues((prevState) => ({ ...prevState, brandColor: primaryColor }));
      })
      .catch((error) => {
        errorLog(error, "ClubsDrawer", "getThemeColor", "theme");
        snackBar.setOptions({
          alertMessage: error.response?.data?.error || error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  const onClose = () => {
    onCloseDrawer();
  };

  const handleSubmit = () => {
    const errors = formValidator({ schema: _schema, values });
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
    } else {
      let query = CREATE_CLUB_SA;
      let variables = {
        name: values.name,
        abbreviation: values.abbreviation,
        founded: +values.founded,
        country: values.country,
        sport: values.sport,
        timezone: "Europe/Athens",
        // location: {
        //   type: "Point",
        //   address: values.location.address,
        //   coordinates: [values.location.lng, values.location.lat],
        // }
      };

      if (selectedClub) {
        query = EDIT_CLUB;
        variables = roles.includes(USER_ROLES.SUPER_ADMIN)
          ? {
              clubId: selectedClub._id,
              name: values.name,
              abbreviation: values.abbreviation || "",
              founded: +values.founded || 0,
              country: values.country || "",
              sport: values.sport,
              timezone: "Europe/Athens",
            }
          : {
              clubId: selectedClub._id,
              name: values.name,
              abbreviation: values.abbreviation || "",
              founded: +values.founded || 0,
              country: values.country || "",
              sport: values.sport,
              timezone: "Europe/Athens",
            };
      }

      axiosGraphQL({ query, variables })
        .then((response) => {
          if (query === CREATE_CLUB_SA) {
            setThemeColor(
              response.data.data.createClubSA._id,
              values.brandColor,
              false
            );
          } else {
            setThemeColor(selectedClub._id, values.brandColor, true);
          }
        })
        .catch((error) => {
          errorLog(
            error,
            "ClubsDrawer",
            "handleSubmit",
            Object.keys({ query })[0]
          );
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    }
  };

  const setThemeColor = (clubId, selectedBrandColor, isEdit) => {
    axiosAuthenticatedPost(`/theme/${clubId}`, { primaryColor: selectedBrandColor })
      .then(async () => await uploadClubLogo(clubId, isEdit))
      .catch((error) => {
        errorLog(error, "ClubsDrawer", "setThemeColor", "theme");
        snackBar.setOptions({
          alertMessage: error.response?.data?.error || error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  const uploadClubLogo = async (clubId, isEdit) => {
    const alertMessage = t(
      isEdit ? "academyUpdatedSuccessfully" : "academyCreatedSuccessfully"
    );

    if (values.initialClubLogo && values.initialClubLogo.length > 0) {
      const shouldBeDeleted =
        values.clubLogo.length === 0 ||
        (values.clubLogo[0].name !== undefined &&
          values.clubLogo[0].name !==
            values.initialClubLogo[0].split("/").pop());
      if (shouldBeDeleted) {
        const deleteVariables = {
          deleteType: FILE_TYPES.clubLogo,
          itemId: clubId,
          fileName: values.initialClubLogo[0].split("/").pop(),
        };
        try {
          await axiosGraphQL({
            query: DELETE_IMAGE,
            variables: deleteVariables,
          });
        } catch (error) {
          errorLog(error, "ClubsDrawer", "uploadClubLogo", "DELETE_IMAGE");
          snackBar.setOptions({
            alertMessage: t("error_uploading_image"),
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
          return;
        }
      }
    }

    if (
      values.clubLogo &&
      values.clubLogo.length > 0 &&
      values.clubLogo[0].name
    ) {
      const variables = {
        uploadType: FILE_TYPES.clubLogo,
        itemId: clubId,
        fileName: values.clubLogo[0].name,
        size: values.clubLogo[0].size,
      };
      axiosGraphQL({ query: UPLOAD_IMAGE, variables })
        .then((response) => {
          const reader = new FileReader();
          reader.onload = function () {
            const arrayBuffer = this.result;
            const array = new Uint8Array(arrayBuffer);
            const blob = new Blob([array]);

            fetch(response.data.data.uploadImage, {
              method: "PUT",
              body: blob,
            })
              .then(async (uploadResponse) => {
                if (!uploadResponse.ok) throw new Error(t("error_uploading_image"));
                snackBar.setOptions({
                  alertMessage,
                  alertSeverity: "success",
                });
                snackBar.setSnackbarOpen(true);
                await getClubs();
                onClose();
              })
              .catch((error) => {
                errorLog(
                  error,
                  "ClubsDrawer",
                  "uploadClubLogo",
                  "UPLOAD_IMAGE"
                );
                snackBar.setOptions({
                  alertMessage: t("error_uploading_image"),
                  alertSeverity: "error",
                });
                snackBar.setSnackbarOpen(true);
              });
          };
          reader.readAsArrayBuffer(values.clubLogo[0]);
        })
        .catch((error) => {
          errorLog(error, "ClubsDrawer", "uploadClubLogo", "UPLOAD_IMAGE");
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    } else {
      snackBar.setOptions({
        alertMessage,
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);
      await getClubs();
      onClose();
    }
  };

  const uploadGdprDocument = async (file) => {
    if (!file || !selectedClub?._id) return;

    setGdprUploading(true);
    let uploadedFileId;
    try {
      const variables = {
        fileName: file.name,
        club: selectedClub._id,
        itemId: selectedClub._id,
        uploadType: FILE_TYPES.clubGdpr,
        size: Number(file.size) || 0,
      };

      const uploadResponse = await axiosGraphQL({
        query: UPLOAD_CLUB_GDPR_FILE,
        variables,
      });
      uploadedFileId = uploadResponse.data.data.uploadFile.fileId;

      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(new Error(t("gdpr_document_upload_failed")));
        reader.readAsArrayBuffer(file);
      });

      const uploadDataObj = JSON.parse(
        uploadResponse.data.data.uploadFile.uploadData
      );
      const formData = new FormData();
      Object.keys(uploadDataObj.fields).forEach((key) => {
        formData.append(key, uploadDataObj.fields[key]);
      });
      formData.append("file", new Blob([new Uint8Array(arrayBuffer)]));

      const response = await fetch(uploadDataObj.url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(t("gdpr_document_upload_failed"));
      }

      const gdprUrl = uploadDataObj.fields?.key
        ? `${uploadDataObj.url}/${uploadDataObj.fields.key}`
        : values.gdpr;

      setValues((prevState) => ({ ...prevState, gdpr: gdprUrl }));
      await getClubs();
      snackBar.setOptions({
        alertMessage: t("gdpr_document_uploaded"),
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);
    } catch (error) {
      if (uploadedFileId) {
        try {
          await axiosGraphQL({
            query: DELETE_FILE,
            variables: { fileId: uploadedFileId },
          });
        } catch (deleteError) {
          errorLog(
            deleteError,
            "ClubsDrawer",
            "uploadGdprDocument",
            "DELETE_FILE"
          );
        }
      }
      errorLog(error, "ClubsDrawer", "uploadGdprDocument", "UPLOAD_FILE");
      snackBar.setOptions({
        alertMessage: error.message || t("gdpr_document_upload_failed"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setGdprUploading(false);
      if (gdprInputRef.current) gdprInputRef.current.value = "";
    }
  };

  const handleGdprFileChange = (event) => {
    uploadGdprDocument(event.target.files?.[0]);
  };

  const onChange = ({ target: { name, value } }) => {
    setValues((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <Drawer
      anchor="right"
      open={openDrawer}
      onClose={onClose}
      SlideProps={{ onExited }}
      PaperProps={getTransactionalDrawerPaperProps(
        "clubs-drawer-title",
        "wide"
      )}
    >
      <DrawerLayout sx={{ pt: isImpersonating ? 3 : 0 }}>
        <DrawerTitleHeader
          closeLabel={t("close")}
          onClose={onClose}
          subtitle={t(
            selectedClub
              ? "edit_club_description"
              : "create_club_description"
          )}
          title={title || (selectedClub ? t("edit_club") : t("create_a_club"))}
          titleId="clubs-drawer-title"
        />
        <DrawerBody data-testid="clubs-drawer-body">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DrawerSection
                description={t("club_details_description")}
                title={t("club_details_title")}
                titleId="club-details-section-title"
              >
                <Form
                  schema={clubDetailsSchema}
                  values={values}
                  onChange={onChange}
                  errors={values.errors}
                />
              </DrawerSection>
            </Grid>
            <Grid item xs={12}>
              <DrawerSection
                description={t("club_branding_description")}
                title={t("club_branding_title")}
                titleId="club-branding-section-title"
              >
                <Form
                  schema={clubBrandingSchema}
                  values={values}
                  onChange={onChange}
                  errors={values.errors}
                />
              </DrawerSection>
            </Grid>
            {selectedClub ? (
              <Grid item xs={12}>
                <DrawerSection
                  description={t("gdpr_document_description")}
                  title={t("gdpr_document")}
                  titleId="gdpr-document-section-title"
                >
                  <Box className={styles.gdprActions}>
                    {values.gdpr ? (
                      <Link
                        href={values.gdpr}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("view_gdpr_document")}
                      </Link>
                    ) : (
                      <span className={styles.identityComponentDetails}>
                        {t("no_gdpr_document")}
                      </span>
                    )}
                    <input
                      ref={gdprInputRef}
                      type="file"
                      accept="application/pdf,.pdf,.doc,.docx,.txt"
                      hidden
                      onChange={handleGdprFileChange}
                    />
                    <Button
                      startIcon={<UploadOutlinedIcon />}
                      variant="outlined"
                      onClick={() => gdprInputRef.current?.click()}
                      disabled={gdprUploading}
                    >
                      {values.gdpr
                        ? t("replace_gdpr_document")
                        : t("upload_gdpr_document")}
                    </Button>
                  </Box>
                </DrawerSection>
              </Grid>
            ) : null}
          </Grid>
        </DrawerBody>
        <DrawerFooter data-testid="clubs-drawer-footer">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" fullWidth onClick={onClose} disabled={gdprUploading}>
              {t("cancel")}
            </Button>
            <Button
              startIcon={selectedClub ? <CheckOutlinedIcon /> : <AddOutlinedIcon />}
              onClick={handleSubmit}
              fullWidth
              disabled={gdprUploading}
            >
              {selectedClub ? t("save") : t("create")}
            </Button>
          </Stack>
        </DrawerFooter>
      </DrawerLayout>
    </Drawer>
  );
};

export default ClubsDrawer;
